/**
 * LeadCalendarView — the Calendar Lens (AD-046 §7.4). Month grid on desktop
 * (Monday-start), each lead on its next-action date; chips coloured by the
 * folded source. v1 anchors on the existing dated seams via
 * `calendarBucket()` (consultBookedAt → discoveryDoneAt → updatedAt) — locked
 * L-6, no new `nextActionAt` field. Undated leads sit in an "Undated" tray.
 * Mobile (≤860px) → an agenda list grouped by date.
 */
import React, { useMemo, useState } from 'react';
import { type Lead } from '../../lib/leads';
import { groupByCalendarDate, sourceColour, sourceMap } from '../../lib/leadLenses';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Monday-index (0=Mon … 6=Sun) for a JS getDay() (0=Sun). */
function mondayIdx(jsDay: number): number {
  return (jsDay + 6) % 7;
}

export default function LeadCalendarView({
  leads,
  onSelect,
  selectedId,
  isMobile,
  nowISO,
}: {
  leads: Lead[];
  onSelect: (id: string) => void;
  /** Currently-open lead — its chip(s) get the `selected` treatment.
   *  Optional so the dev-harness can mount the view. */
  selectedId?: string | null;
  isMobile: boolean;
  nowISO: string;
}) {
  const initial = new Date(nowISO);
  const [year, setYear] = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth()); // 0-based

  const byDate = useMemo(() => groupByCalendarDate(leads), [leads]);
  const undated = byDate[''] ?? [];

  function Chip({ l }: { l: Lead }) {
    const selected = l.id === selectedId;
    return (
      <button
        type="button"
        data-lead-id={l.id}
        onClick={() => onSelect(l.id)}
        title={`${l.name} · ${sourceMap(l.source)}`}
        aria-current={selected ? true : undefined}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          fontSize: 9.8,
          fontWeight: 700,
          padding: '2px 5px',
          marginBottom: 2,
          borderRadius: 4,
          // Selected: 2px accent border + soft accent elevation (source stripe kept).
          border: selected ? '2px solid var(--accent, #008069)' : `1px solid ${sourceColour(l.source)}`,
          borderLeft: `3px solid ${sourceColour(l.source)}`,
          background: 'var(--paper, #fff)',
          boxShadow: selected ? '0 8px 22px -10px rgba(0,128,105,.55)' : undefined,
          color: 'var(--ink, #1A1A1A)',
          cursor: 'pointer',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {l.name}
      </button>
    );
  }

  /* ── Mobile: agenda list grouped by date ── */
  if (isMobile) {
    const dates = Object.keys(byDate).filter((k) => k).sort();
    return (
      <div style={{ padding: 12 }}>
        {dates.length === 0 && undated.length === 0 ? (
          <div style={emptyS}>No dated leads.</div>
        ) : (
          dates.map((d) => (
            <div key={d} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-soft, #4B5563)', margin: '4px 2px' }}>{d}</div>
              {byDate[d].map((l) => <Chip key={l.id} l={l} />)}
            </div>
          ))
        )}
        {undated.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-mute, #6B7280)', margin: '4px 2px' }}>Undated · {undated.length}</div>
            {undated.map((l) => <Chip key={l.id} l={l} />)}
          </div>
        )}
      </div>
    );
  }

  /* ── Desktop: month grid ── */
  const first = new Date(year, month, 1);
  const lead0 = mondayIdx(first.getDay());
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ d: Date; key: string } | null> = [];
  for (let i = 0; i < lead0; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    cells.push({ d, key: ymd(d) });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  function prevMonth() {
    if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1);
  }
  const monthLabel = first.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" onClick={prevMonth} style={navBtn}>‹</button>
        <div style={{ fontWeight: 800, fontSize: 14 }}>{monthLabel}</div>
        <button type="button" onClick={nextMonth} style={navBtn}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {DOW.map((d) => (
          <div key={d} style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-mute, #6B7280)', textAlign: 'center', padding: '2px 0' }}>{d}</div>
        ))}
        {cells.map((c, i) => (
          <div
            key={i}
            style={{
              minHeight: 76,
              border: '1px solid var(--rule, #E5E7EB)',
              borderRadius: 'var(--r-sm, 9px)',
              background: c ? 'var(--paper, #fff)' : 'var(--inset, #F6F7F9)',
              padding: 3,
              overflow: 'hidden',
            }}
          >
            {c && (
              <>
                <div style={{ fontSize: 10, color: 'var(--ink-mute, #6B7280)', marginBottom: 2 }}>{c.d.getDate()}</div>
                {(byDate[c.key] ?? []).slice(0, 4).map((l) => <Chip key={l.id} l={l} />)}
                {(byDate[c.key]?.length ?? 0) > 4 && (
                  <div style={{ fontSize: 9, color: 'var(--ink-mute, #6B7280)' }}>+{byDate[c.key].length - 4} more</div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
      {undated.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-mute, #6B7280)', margin: '4px 2px' }}>Undated · {undated.length}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {undated.map((l) => (
              <div key={l.id} style={{ width: 150 }}><Chip l={l} /></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 'var(--r-pill, 999px)',
  border: '1px solid var(--rule-strong, #D1D5DB)',
  background: 'var(--paper, #fff)',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
};
const emptyS: React.CSSProperties = {
  padding: '30px 12px',
  textAlign: 'center',
  color: 'var(--ink-mute, #6B7280)',
  fontSize: 13,
};
